import { Grid, IconButton, makeStyles, Typography } from '@material-ui/core';
import { Input as InputIcon } from '@material-ui/icons';
import clsx from 'clsx';
import { SearchBar } from 'components/SearchBar';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import Permission from 'components/ValidPermission';
import { useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import { PermissionsData } from 'modules/authenticate/interfaces';
import { modalClose } from 'modules/modal/actions';
import { getEnvironmentsWithFailedZeroTrustRequest } from 'modules/monitor/actions';
import {
  EnvironmentsWithFailedZeroTrustResponse,
  EnvironmentWithFailedZeroTrustFields,
} from 'modules/monitor/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import ApplicationState from 'store/interfaces';
import { formatPercent } from 'utils/helpers';
import styles from './styles';

const useStyles = makeStyles(styles);

interface Props {
  environmentsWithFailedZeroTrust: EnvironmentsWithFailedZeroTrustResponse;
  isLoading: boolean;
  permissions?: PermissionsData;
  modalClose: () => void;
  getEnvironmentsWithFailedZeroTrustRequest: (props) => void;
}

const EnvironmentsWithFailedZeroTrustCard = ({
  environmentsWithFailedZeroTrust,
  isLoading,
  permissions,
  modalClose,
  getEnvironmentsWithFailedZeroTrustRequest,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const {
    itemOrder,
    searchValue,
    query,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters();

  const headers: Array<TableCellHeaderProps & { hidden?: boolean }> = [
    {
      key: '',
      i18n: 'ACCESS_KEY',
      centerTitle: true,
      hidden: !permissions?.list_environment_instances,
    },
    {
      key: 'environment',
      i18n: 'ENVIRONMENT_KEY',
      order: true,
    },
    {
      key: 'partner',
      i18n: 'PARTNER_KEY',
      order: true,
    },
    {
      key: 'total_sessions_with_failed_zero_trust',
      i18n: 'MONITOR.TOTAL_FAILED_SESSIONS_KEY',
      order: true,
    },
    {
      key: 'total_sessions',
      i18n: 'SESSIONS_KEY',
      order: true,
    },
    {
      key: 'sessions_with_failed_zero_trust_percentage',
      i18n: 'MONITOR.TOTAL_FAILED_SESSIONS_PERCENTAGE_KEY',
      order: true,
    },
  ].filter(item => !item.hidden);

  React.useEffect(() => {
    getEnvironmentsWithFailedZeroTrustRequest({ query });
  }, [getEnvironmentsWithFailedZeroTrustRequest, query]);

  const redirectToEnvironment = (
    item: EnvironmentWithFailedZeroTrustFields
  ) => {
    history.push(`/environments/${item?.environment?.uuid}/instances`);
  };

  return (
    <Grid>
      <Grid>
        <SearchBar
          id="search-environments"
          isLoading={isLoading}
          searchValue={searchValue}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
        />
        <TableItems
          header={headers.map(item => (
            <TableCell
              centerTitle={item?.centerTitle}
              key={item?.key}
              order={item?.order}
              markOderItem={itemOrder === item?.key}
              handleOrder={() => (item?.order ? handleOrder(item?.key) : null)}
            >
              {i18n.t(item?.i18n)}
            </TableCell>
          ))}
          renderItems={(item: EnvironmentWithFailedZeroTrustFields) => (
            <TableRow key={item.environment.uuid}>
              <Permission value="list_environment_instances">
                <TableCell textAlignCenter>
                  <IconButton
                    className={classes.accessButton}
                    onClick={() => {
                      redirectToEnvironment(item);
                      modalClose();
                    }}
                  >
                    <InputIcon />
                  </IconButton>
                </TableCell>
              </Permission>
              <TableCell>{item.environment.name}</TableCell>
              <TableCell>{item.partner.name}</TableCell>
              <TableCell textAlignCenter>
                {item.total_sessions_with_failed_zero_trust}
              </TableCell>
              <TableCell textAlignCenter>{item.total_sessions}</TableCell>
              <TableCell centerTitle>
                <Typography
                  className={clsx(classes.failedSessionsPercentage, {
                    [classes.red]:
                      item.sessions_with_failed_zero_trust_percentage > 50,
                  })}
                >
                  {formatPercent(
                    item.sessions_with_failed_zero_trust_percentage
                  )}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          data={environmentsWithFailedZeroTrust}
          isLoading={isLoading}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('ENVIRONMENTS_PAGE.NO_ENVIRONMENT_KEY')}
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  monitor: {
    environmentsWithFailedZeroTrust,
    isLoadingEnvironmentsWithFailedZeroTrust,
  },
}: ApplicationState) => ({
  environmentsWithFailedZeroTrust,
  isLoading: isLoadingEnvironmentsWithFailedZeroTrust,
  permissions: data?.permissions,
});

const mapDispatchToProps = {
  modalClose,
  getEnvironmentsWithFailedZeroTrustRequest,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EnvironmentsWithFailedZeroTrustCard);
